import { useState, type ReactNode } from 'react';
import { Button } from '@/components/ui/button';
import { Dialog } from '@/components/ui/dialog';
import { Field, Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import { useSaveCreditFeeTemplate } from '@/hooks/useReference';
import type { CreditFeeTemplate } from '@/types';

/**
 * The one credit/fee template form: Manage templates edits with it, and the
 * template pickers (credit/fee dialog, invoice editor) add with it.
 */
export function TemplateDialog({
  template,
  prefillName = '',
  saving,
  onClose,
  onSave
}: {
  template: CreditFeeTemplate | 'new' | null;
  /** Text typed into the picker before choosing "Create", used as the name. */
  prefillName?: string;
  saving?: boolean;
  onClose: () => void;
  onSave: (input: Omit<CreditFeeTemplate, 'id'>) => void;
}) {
  const existing = template && template !== 'new' ? template : null;
  const [seeded, setSeeded] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [kind, setKind] = useState<'credit' | 'fee'>('fee');
  const [amount, setAmount] = useState('');

  const seedKey = existing?.id ?? (template === 'new' ? `new:${prefillName}` : null);
  if (template && seeded !== seedKey) {
    setSeeded(seedKey);
    setName(existing?.name ?? prefillName);
    setKind(existing?.kind ?? 'fee');
    setAmount(existing ? String(existing.amount) : '');
  }

  const amountValue = Number(amount) || 0;
  const valid = name.trim().length > 0 && amountValue > 0;

  return (
    <Dialog
      open={Boolean(template)}
      onOpenChange={(v) => !v && !saving && onClose()}
      title={existing ? 'Edit template' : 'New template'}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            variant="primary"
            disabled={!valid}
            loading={saving}
            onClick={() =>
              onSave({
                name: name.trim(),
                kind,
                amount: amountValue,
                isHourly: false,
                description: null
              })
            }
          >
            Save
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <Field label="Name">
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Filing fee"
            autoFocus
          />
        </Field>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Type">
            <Select
              className="w-full"
              value={kind}
              onChange={(v) => setKind(v as 'credit' | 'fee')}
              options={[
                { value: 'fee', label: 'Fee' },
                { value: 'credit', label: 'Credit' }
              ]}
            />
          </Field>
          <Field label="Amount">
            <Input
              className="text-right tnum"
              inputMode="decimal"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="0.00"
            />
          </Field>
        </div>
      </div>
    </Dialog>
  );
}

/**
 * "Add new" for a template picker: hand `open` to the Combobox's `onAddNew`,
 * render `dialog` next to it, and get the saved template back.
 */
export function useAddTemplate(onCreated: (template: CreditFeeTemplate) => void): {
  open: (prefill?: string) => void;
  dialog: ReactNode;
} {
  const [prefill, setPrefill] = useState<string | null>(null);
  const save = useSaveCreditFeeTemplate();

  return {
    open: (text = '') => setPrefill(text),
    dialog: (
      <TemplateDialog
        template={prefill === null ? null : 'new'}
        prefillName={prefill ?? ''}
        saving={save.isPending}
        onClose={() => setPrefill(null)}
        onSave={async (input) => {
          let created: CreditFeeTemplate;
          try {
            created = await save.mutateAsync(input);
          } catch {
            // useSaveCreditFeeTemplate already toasted; keep the form open.
            return;
          }
          setPrefill(null);
          onCreated(created);
        }}
      />
    )
  };
}
